<template>
  <div v-if="isVisible" class="filter-overlay">
    <div class="filter-overlay-backdrop" @click="close"></div>
    <div class="filter-overlay-content">
      <div class="filter-header">
        <h2>Import Devices</h2>
      </div>

      <!-- Carousel Navigation -->
      <div class="carousel-navigation">
        <div
            v-for="(step, index) in steps"
            :key="index"
            :class="['step-indicator', { active: currentStep === index, completed: currentStep > index }]"
        >
          <div class="step-number">{{ index + 1 }}</div>
          <div class="step-name">{{ step }}</div>
        </div>
      </div>

      <!-- Form Steps -->
      <div class="form-steps">
        <!-- Step 0: Select Import Type -->
        <div v-if="currentStep === 0" class="form-step">
          <div class="form-group">
            <div class="filter-label-container">
              <label class="filter-label">File Type</label>
            </div>
            <div class="toggle-selection-wrapper">
              <button
                  :class="['toggle-option', { active: importType === 'csv' }]"
                  @click="importType = 'csv'"
              >
                CSV
              </button>
              <button
                  :class="['toggle-option', { active: importType === 'json' }]"
                  @click="importType = 'json'"
              >
                JSON
              </button>
            </div>
          </div>
        </div>

        <!-- Step 1: Select Device Type, Protocol & MPID Toggle -->
        <div v-if="currentStep === 1" class="form-step">
          <!-- Device Type Selection -->
          <div class="form-group">
            <div class="filter-label-container">
              <label class="filter-label">Device Type</label>
            </div>
            <div class="toggle-selection-wrapper">
              <button
                  :class="['toggle-option', { active: deviceType === 'mechanical' }]"
                  @click="selectDeviceType('mechanical')"
              >
                Mechanical
              </button>
              <button
                  :class="['toggle-option', { active: deviceType === 'integrated' }]"
                  @click="selectDeviceType('integrated')"
              >
                Integrated
              </button>
              <button
                  :class="['toggle-option', { active: deviceType === 'addon' }]"
                  @click="selectDeviceType('addon')"
              >
                Addon
              </button>
            </div>
          </div>

          <!-- Protocol Selection (for Integrated devices) -->
          <div v-if="deviceType === 'integrated'" class="form-group">
            <div class="filter-label-container">
              <label class="filter-label">Protocol</label>
            </div>
            <div class="toggle-selection-wrapper">
              <button
                  :class="['toggle-option', { active: protocol === 'DLMS' }]"
                  @click="protocol = 'DLMS'"
              >
                DLMS
              </button>
              <button
                  :class="['toggle-option', { active: protocol === 'LORA_WBUS' }]"
                  @click="protocol = 'LORA_WBUS'"
              >
                LORA & WBUS
              </button>
              <button
                  :class="['toggle-option', { active: protocol === 'CTR' }]"
                  @click="protocol = 'CTR'"
              >
                CTR
              </button>
            </div>
          </div>

          <!-- Protocol Selection (for Addon devices) -->
          <div v-if="deviceType === 'addon'" class="form-group">
            <div class="filter-label-container">
              <label class="filter-label">Protocol</label>
            </div>
            <div class="toggle-selection-wrapper">
              <button
                  :class="['toggle-option', { active: protocol === 'DLMS' }]"
                  @click="protocol = 'DLMS'"
              >
                DLMS
              </button>
              <button
                  :class="['toggle-option', { active: protocol === 'LORA' }]"
                  @click="protocol = 'LORA'"
              >
                LORA
              </button>
              <button
                  :class="['toggle-option', { active: protocol === 'LORA_WBUS' }]"
                  @click="protocol = 'LORA_WBUS'"
              >
                LORA & WBUS
              </button>
              <button
                  :class="['toggle-option', { active: protocol === 'CTR' }]"
                  @click="protocol = 'CTR'"
              >
                CTR
              </button>
            </div>
          </div>

          <!-- MPID Toggle -->
          <div class="form-group">
            <div class="filter-label-container">
              <label class="filter-label">Import MPID?</label>
              <div class="hint-icon" title="If you select Yes, ensure that the MPID is already created. Devices without a valid MPID will not be uploaded.">?</div>
            </div>
            <div class="toggle-selection-wrapper">
              <button
                  :class="['toggle-option', { active: importMPID === true }]"
                  @click="importMPID = true"
              >
                Yes
              </button>
              <button
                  :class="['toggle-option', { active: importMPID === false }]"
                  @click="importMPID = false"
              >
                No
              </button>
            </div>
            <small v-if="importMPID" style="color: #f44336; font-size: 9px; margin-top: 25px; display: block;">
              Warning: MPID must already be created. If any device is missing the MPID, the upload will fail.
            </small>
          </div>
        </div>

        <!-- Step 2: Template & Delimiter or JSON Example -->
        <div v-if="currentStep === 2" class="form-step">
          <div v-if="importType === 'csv'">
            <div class="form-group">
              <div class="filter-label-container">
                <label class="filter-label">CSV Delimiter</label>
              </div>
              <div class="toggle-selection-wrapper">
                <button
                    :class="['toggle-option', { active: delimiter === ',' }]"
                    @click="delimiter = ','"
                >
                  Comma (,)
                </button>
                <button
                    :class="['toggle-option', { active: delimiter === ';' }]"
                    @click="delimiter = ';'"
                >
                  Semicolon (;)
                </button>
                <button
                    :class="['toggle-option', { active: delimiter === '\t' }]"
                    @click="delimiter = '\t'"
                >
                  Tab
                </button>
                <br>
                <button class="action-button" @click="downloadTemplate">
                  Download CSV Template
                </button>
              </div>
            </div>
          </div>
          <div v-else-if="importType === 'json'">
            <button class="action-button" @click="downloadJsonTemplate">
              Download JSON Example Template
            </button>
          </div>
        </div>

        <!-- Step 3: Upload File -->
        <div v-if="currentStep === 3" class="form-step">
          <input
              type="file"
              @change="handleFileSelect"
              accept=".csv,.json"
              class="search-input"
          />
          <div v-if="selectedFile" class="file-info">
            <p>Selected file: {{ selectedFile.name }}</p>
            <p>Size: {{ formatFileSize(selectedFile.size) }}</p>
          </div>
        </div>
      </div>

      <!-- Navigation Buttons -->
      <div class="filter-actions">
        <button v-if="currentStep > 0" class="action-button" @click="currentStep--">
          Back
        </button>
        <button class="action-button" @click="close">Cancel</button>
        <button
            v-if="currentStep < steps.length - 1"
            class="action-button"
            @click="currentStep++"
            :disabled="!canProceed"
        >
          Next
        </button>
        <button
            v-else
            class="action-button submit-button"
            @click="submitImport"
            :disabled="!canProceed || !selectedFile || isLoading"
        >
          {{ isLoading ? 'Importing...' : 'Import Devices' }}
        </button>
      </div>

      <!-- Loading/Error Messages -->
      <div v-if="errorMessage" class="error-message">
        {{ errorMessage }}
      </div>

      <!-- Success/Warning Messages -->
      <div v-if="importResult" class="import-result">
        <div v-if="importResult.imported > 0" class="success-message">
          ✓ Successfully imported {{ importResult.imported }} devices
          <span v-if="importResult.chirpstack_created > 0">
            ({{ importResult.chirpstack_created }} created in lora server)
          </span>
        </div>
        <div v-if="importResult.chirpstack_warnings && importResult.chirpstack_warnings.length > 0" class="warning-section">
          <ul>
            <li v-for="(warning, index) in importResult.chirpstack_warnings" :key="index">
              {{ warning }}
            </li>
          </ul>
        </div>
        <div v-if="importResult.errors && importResult.errors.length > 0" class="error-section">
          <h4>❌ Errors:</h4>
          <ul>
            <li v-for="(error, index) in importResult.errors" :key="index">
              {{ error }}
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ImportDevicesOverlay',
  props: {
    isVisible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      currentStep: 0,
      steps: ['File Type', 'Device Config', 'Template', 'Upload'],
      importType: 'csv',
      deviceType: 'mechanical',
      protocol: '',
      importMPID: false,
      delimiter: ',',
      selectedFile: null,
      isLoading: false,
      errorMessage: '',
      importResult: null
    };
  },
  computed: {
    canProceed() {
      switch (this.currentStep) {
        case 0:
          return this.importType !== '';
        case 1:
          if (this.deviceType === 'mechanical') {
            return true;
          }
          return this.protocol !== '';
        case 2:
          return true;
        case 3:
          return this.selectedFile !== null;
        default:
          return false;
      }
    },
    // Helper: true when the selected protocol uses NB-IoT SIM (ICCID)
    isNbIotProtocol() {
      return this.protocol === 'DLMS' || this.protocol === 'CTR';
    }
  },
  methods: {
    selectDeviceType(type) {
      this.deviceType = type;
      this.protocol = '';
      if (type === 'integrated') {
        this.protocol = 'DLMS';
      } else if (type === 'addon') {
        this.protocol = 'LORA';
      }
    },

    handleFileSelect(event) {
      const file = event.target.files[0];
      if (file) {
        const fileExtension = file.name.split('.').pop().toLowerCase();
        const expectedExtension = this.importType;
        if (fileExtension !== expectedExtension) {
          this.errorMessage = `Please select a ${expectedExtension.toUpperCase()} file`;
          this.selectedFile = null;
          event.target.value = '';
          return;
        }
        this.selectedFile = file;
        this.errorMessage = '';
      }
    },

    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },

    downloadTemplate() {
      let headers = [
        'LogicalDeviceNumber',
        'MPID',
        'IDRadio',
        'CustomerNumber',
        'LoRaEUI',
        'Type',
        'IsIntegrated',
        'DistributionCompany',
        'Caliber'
      ];

      if (this.deviceType !== 'mechanical') {
        headers.push('Protocol');

        if (this.protocol === 'CTR') {
          headers.push('CTRKey');
          headers.push('ICCID'); // NB-IoT SIM — optional
        } else if (this.protocol === 'DLMS') {
          headers.push('DLMSKey');
          headers.push('ICCID'); // NB-IoT SIM — optional
        } else if (this.protocol === 'LORA') {
          headers.push('LoRaKey', 'LoRaNTWKey');
        } else if (this.protocol === 'LORA_WBUS') {
          headers.push('LoRaKey', 'LoRaNTWKey', 'WBusKey');
        }
      }

      let sampleData = {
        LogicalDeviceNumber: 'DEV001',
        MPID: this.importMPID ? 'MP001' : '',
        IDRadio: 'RAD001',
        CustomerNumber: 'CUST001',
        LoRaEUI: '0123456789ABCDEF',
        Type: this.deviceType,
        IsIntegrated: this.deviceType === 'integrated' ? 'true' : 'false',
        DistributionCompany: 'Company A',
        Caliber: '15'
      };

      if (this.deviceType !== 'mechanical') {
        sampleData.Protocol = this.protocol;

        if (this.protocol === 'CTR') {
          sampleData.CTRKey = 'ctr-key-example';
          sampleData.ICCID = ''; // leave empty if no SIM
        } else if (this.protocol === 'DLMS') {
          sampleData.DLMSKey = 'dlms-key-example';
          sampleData.ICCID = ''; // leave empty if no SIM
        } else if (this.protocol === 'LORA') {
          sampleData.LoRaKey = 'lora-app-key-example';
          sampleData.LoRaNTWKey = 'lora-ntw-key-example';
        } else if (this.protocol === 'LORA_WBUS') {
          sampleData.LoRaKey = 'lora-app-key-example';
          sampleData.LoRaNTWKey = 'lora-ntw-key-example';
          sampleData.WBusKey = 'wbus-key-example';
        }
      }

      let csvContent = headers.join(this.delimiter) + '\n';
      csvContent += headers.map(h => sampleData[h] || '').join(this.delimiter) + '\n';

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `device_import_template_${this.deviceType}_${this.protocol || 'noprotocol'}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },

    downloadJsonTemplate() {
      let exampleDevices = [];

      let baseDevice = {
        LogicalDeviceNumber: "DEV001",
        Type: this.deviceType,
        IDRadio: "RAD001",
        CustomerNumber: "CUST001",
        LoRaEUI: "0123456789ABCDEF",
        IsIntegrated: this.deviceType === 'integrated',
        DistributionCompany: "Company A",
        Caliber: "15"
      };

      if (this.importMPID) {
        baseDevice.MPID = "MP001";
      }

      if (this.deviceType !== 'mechanical') {
        baseDevice.Protocol = this.protocol;

        if (this.protocol === 'CTR') {
          baseDevice.CTRKey = "ctr-key-example";
          baseDevice.ICCID = ""; // optional — NB-IoT SIM card number
        } else if (this.protocol === 'DLMS') {
          baseDevice.DLMSKey = "dlms-key-example";
          baseDevice.ICCID = ""; // optional — NB-IoT SIM card number
        } else if (this.protocol === 'LORA') {
          baseDevice.LoRaKey = "lora-app-key-example";
          baseDevice.LoRaNTWKey = "lora-ntw-key-example-optional";
        } else if (this.protocol === 'LORA_WBUS') {
          baseDevice.LoRaKey = "lora-app-key-example";
          baseDevice.LoRaNTWKey = "lora-ntw-key-example-optional";
          baseDevice.WBusKey = "wbus-key-example";
        }
      }

      exampleDevices.push(baseDevice);

      // Second example device — shows ICCID filled in for DLMS/CTR
      let secondDevice = JSON.parse(JSON.stringify(baseDevice));
      secondDevice.LogicalDeviceNumber = "DEV002";
      secondDevice.IDRadio = "RAD002";
      secondDevice.LoRaEUI = "FEDCBA9876543210";
      if (this.importMPID) {
        secondDevice.MPID = "MP002";
      }
      if (this.isNbIotProtocol) {
        secondDevice.ICCID = "89391030000000000000"; // example with actual ICCID
      }
      exampleDevices.push(secondDevice);

      const jsonContent = JSON.stringify(exampleDevices, null, 2);

      const blob = new Blob([jsonContent], { type: 'application/json' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `device_import_template_${this.deviceType}_${this.protocol || 'noprotocol'}.json`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },

    async submitImport() {
      if (!this.selectedFile) return;

      this.isLoading = true;
      this.errorMessage = '';
      this.importResult = null;

      const formData = new FormData();
      formData.append('file', this.selectedFile);
      formData.append('importType', this.importType);
      formData.append('deviceType', this.deviceType);
      formData.append('importMPID', this.importMPID.toString());
      formData.append('delimiter', this.delimiter);

      if (this.deviceType !== 'mechanical') {
        formData.append('protocol', this.protocol);
      }

      try {
        const token = localStorage.getItem('access_token');
        if (!token) {
          throw new Error('Authentication token not found. Please log in again.');
        }

        const apiBaseUrl = process.env.VUE_APP_API_URL || '';
        const response = await fetch(`${apiBaseUrl}/devices/import`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        });

        const result = await response.json();

        if (response.ok || response.status === 207) {
          this.importResult = result;
          this.$emit('import-success', result);

          let message = `Successfully imported ${result.imported} devices`;
          if (result.chirpstack_created > 0) {
            message += ` (${result.chirpstack_created} created in Lora server)`;
          }
          if (result.errors && result.errors.length > 0) {
            message += `. ${result.errors.length} errors occurred.`;
          }
          if (result.chirpstack_warnings && result.chirpstack_warnings.length > 0) {
            message += ` Note: Some Lora Server operations had warnings.`;
          }

          if (this.$toast) {
            if (result.errors && result.errors.length > 0) {
              this.$toast.warning(message);
            } else {
              this.$toast.success(message);
            }
          } else {
            alert(message);
          }

          if (!result.errors && !result.chirpstack_warnings) {
            setTimeout(() => this.close(), 2000);
          }
        } else {
          this.errorMessage = result.message || 'Import failed';
          if (result.errors && Array.isArray(result.errors)) {
            this.errorMessage += '\n' + result.errors.join('\n');
          }
        }
      } catch (error) {
        console.error('Import error:', error);
        this.errorMessage = error.message || 'Network error occurred during import';
      } finally {
        this.isLoading = false;
      }
    },

    close() {
      this.$emit('close');
      this.resetForm();
    },

    resetForm() {
      this.currentStep = 0;
      this.importType = 'csv';
      this.deviceType = 'mechanical';
      this.protocol = '';
      this.importMPID = false;
      this.delimiter = ',';
      this.selectedFile = null;
      this.errorMessage = '';
      this.isLoading = false;
      this.importResult = null;
    }
  },
  watch: {
    isVisible(newVal) {
      if (!newVal) {
        this.resetForm();
      }
    }
  }
};
</script>
<style scoped>
/* Overlay */
.filter-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.filter-overlay-backdrop {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(to top, rgba(32, 32, 32, 0.1) 0%, rgba(16, 16, 16, 0.95) 100%);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(4px);
  animation: fadeIn 0.3s ease forwards;
}

.filter-overlay-content {
  position: relative;
  width: 600px;
  max-width: 90%;
  max-height: 90vh;
  border-radius: 8px;
  padding: 24px;
  color: white;
  overflow-y: auto;
  animation: slideIn 0.3s ease forwards;
  z-index: 1001;
}

/* Header */
.filter-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  padding-bottom: 16px;
}

.filter-header h2 {
  font-size: 18px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  margin: 0;
}

/* Carousel/Steps Navigation */
.carousel-navigation {
  display: flex;
  justify-content: space-between;
  margin-bottom: 32px;
  position: relative;
}

.carousel-navigation::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  transform: translateY(-50%);
  z-index: 1;
}

.step-indicator {
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  z-index: 2;
}

.step-number {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 8px;
  border: 2px solid transparent;
  transition: all 0.3s ease;
}

.step-name {
  font-size: 10px;
  font-weight: 500;
  opacity: 0.7;
  text-align: center;
  transition: opacity 0.3s ease;
}

.step-indicator.active .step-number {
  background-color: rgba(79, 195, 247, 0.2);
  border-color: #4fc3f7;
  color: #4fc3f7;
}

.step-indicator.active .step-name {
  opacity: 1;
  color: #4fc3f7;
}

.step-indicator.completed .step-number {
  background-color: rgba(79, 195, 247, 0.5);
  border-color: #4fc3f7;
}

/* Form Steps */
.form-step {
  animation: fadeIn 0.3s ease forwards;
}

.form-group {
  margin-bottom: 20px;
}

.filter-label-container {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.filter-label {
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  opacity: 0.9;
}

.hint-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: rgba(79, 195, 247, 0.3);
  color: #4fc3f7;
  font-size: 7px;
  font-weight: bold;
  cursor: help;
  transition: background-color 0.2s, color 0.2s;
}

.hint-icon:hover {
  background-color: rgba(79, 195, 247, 0.6);
  color: white;
}

/* Input Fields */
.search-input-container {
  position: relative;
  max-width: 100%;
  padding-left: 25px;
}

.search-input {
  width: 100%;
  background: transparent;
  border: none;
  border-bottom: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  padding: 8px 0;
  font-size: 12px;
}

.search-input:focus {
  outline: none;
  border-bottom-color: rgba(255, 255, 255, 0.6);
}

.search-input::placeholder {
  color: rgba(255, 255, 255, 0.3);
}

/* Toggle selection style */
.toggle-selection-wrapper {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-top: 8px;
}

.toggle-option {
  background: none;
  border: none;
  color: white;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  cursor: pointer;
  padding: 0;
  opacity: 0.4;
  transition: opacity 0.2s;
}

.toggle-option:hover,
.toggle-option.active {
  opacity: 1;
}

.toggle-option.active {
  position: relative;
}

.toggle-option.active::after {
  content: '';
  position: absolute;
  bottom: -5px;
  left: 0;
  width: 100%;
  height: 2px;
  background-color: #4fc3f7;
  opacity: 0.7;
}

/* Confirmation Step */
.confirmation-title {
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 20px;
  opacity: 0.9;
}

.confirmation-list {
  background-color: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  padding: 16px;
}

.confirmation-item {
  display: flex;
  margin-bottom: 12px;
  font-size: 14px;
}

.confirmation-label {
  flex: 0 0 40%;
  font-weight: 500;
  opacity: 0.7;
}

.confirmation-value {
  flex: 0 0 60%;
  font-weight: 400;
}

/* Action buttons */
.filter-actions {
  display: flex;
  justify-content: flex-end;
  gap: 16px;
  margin-top: 24px;
  padding-top: 20px;
}

.action-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: none;
  border: none;
  color: white;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  padding: 8px 16px;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.action-button:hover:not(:disabled) {
  background-color: rgba(255, 255, 255, 0.1);
}

.action-button:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.submit-button {
  background-color: rgba(79, 195, 247, 0.2);
}

.submit-button:hover {
  background-color: rgba(79, 195, 247, 0.3);
}

/* Animations */
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
